import React, { useEffect, useRef } from 'react';
import { Globe, Award, Calendar, MapPin, Mail, Phone, Linkedin, Quote } from 'lucide-react';
import './Directors1.css';
import vision from '../Images/vision.jpg'

const Directors = () => {
  const journeyRef = useRef(null);

  const director = {
    id: 1,
    name: 'Sameer Bannerjee',
    position: 'CEO',
    experience: '27+ years',
    education: 'Heavy Equipment Industry Specialist',
    specialization: 'Heavy Machinery & Earthmover Spare Parts',
    previousCompanies: [
      'Larsen & Toubro (L&T)',
      'BEML (Bharat Earth Movers Ltd.)',
      'Tata Hitachi'
    ],
    achievements: [
      'Founded Megapix Machinery with 27+ years industry experience',
      'Led partnerships with India\'s top heavy equipment giants',
      'Established trusted supply chain for heavy machinery spare parts',
      'Built reputation for engineering precision and customer trust'
    ],
    bio: 'Sameer Bannerjee brings over 27 years of industry experience to Megapix Machinery, having worked with some of India\'s top heavy equipment giants including Larsen & Toubro (L&T), BEML, and Tata Hitachi. His hands-on experience and deep understanding of the heavy machinery industry has shaped Megapix Machinery into a trusted partner for infrastructure growth across India.',
    email: 'info@megapixmachinery.co.in',
    phone: '+91 98765 43210',
  };

  const companyJourney = [
    {
      step: '1',
      title: 'Foundation & Vision',
      description: 'Founded with a clear vision to redefine the heavy machinery spare parts industry through innovation, customer-centric values, and a commitment to excellence.'
    },
    {
      step: '2',
      title: 'Product & Service Innovation',
      description: 'Expanded and refined our offerings to meet evolving market needs—launching new product lines, enhancing service quality, and staying ahead of industry trends.'
    },
    {
      step: '3',
      title: 'Digital Transformation',
      description: 'Adopted cutting-edge technologies to streamline operations, elevate customer experience, and enable data-driven decision-making.'
    },
    {
      step: '4',
      title: 'Cultural & Operational Evolution',
      description: 'Grew into a dynamic, agile organization with a strong internal culture, scalable processes, and a focus on continuous improvement.'
    },
    {
      step: '5',
      title: 'Brand Evolution & Future Growth',
      description: 'Evolved our brand to reflect our values and aspirations. We are poised for national expansion, sustainable innovation, and long-term impact.'
    }
  ];

  // Enhanced Journey Animation Effect
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    const journeyItems = document.querySelectorAll('.journey-item');
    journeyItems.forEach(item => {
      observer.observe(item);
    });

    return () => {
      journeyItems.forEach(item => {
        observer.unobserve(item);
      });
    };
  }, []);

  return (
    <div className="directors-page">
      {/* Hero Section */}
      <section className="directors-hero">
        <div className="container">
          <div className="hero-content">
            {/* <h1>Leadership</h1> */}
            <p>
              Meet the visionary leader driving Megapix Machinery towards excellence in heavy machinery and earthmover spare parts.
            </p>
          </div>
        </div>
      </section>

      {/* Founder's Message */}
      <section className="founders-message">
        <div className="container">
          <h2>CEO's Message</h2>
          <div className="quote-section">
            <div className="quote-icon">
              <Quote size={48} />
            </div>
            <blockquote className="main-quote">
              "Quality means doing it right when no one is looking."
            </blockquote>
            <p className="quote-author">— Henry Ford</p>
          </div>
          
          <div className="message-content">
            <p className="message-text">
              Welcome to Megapix Machinery, a trusted name in heavy machinery and earthmover spare parts. With over 27 years of industry experience, we have proudly supported India's infrastructure growth by providing reliable, high-quality components to the toughest environments.
            </p>
            <p className="message-text">
              I am Sameer Bannerjee, and I bring with me a legacy shaped by hands-on experience at some of India's top heavy equipment giants such as Larsen & Toubro (L&T), BEML (Bharat Earth Movers Ltd.), and Tata Hitachi. These institutions instilled in me the values of engineering precision, customer trust, and operational integrity principles we live by at Megapix Machinery.
            </p>
            <p className="message-text">
              Our company stands on the pillars of innovation, durability, and prompt service. Whether you're managing a mining fleet, operating in a construction zone, or engaged in earthmoving projects, we understand your need for uptime and performance. Our spare parts are carefully selected, tested, and supplied to ensure they meet international standards, yet are priced for competitive Indian markets.
            </p>
            <p className="message-text">
              We are not just vendors; we are partners in your progress. I invite you to explore our offerings and experience a support system that is proactive, technical, and transparent.
            </p>
            <div className="message-signature">
              <p><strong>Warm regards,</strong></p>
              <p className="signature-name">Sameer Bannerjee</p>
              <p className="signature-title">CEO, Megapix Machinery</p>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Company Journey */}
      <section className="journey-section" ref={journeyRef}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Journey</h2>
            <p className="journey-intro">
              From foundation to future growth, explore the key milestones that have shaped Megapix Machinery's evolution in the heavy machinery industry.
            </p>
          </div>
          
          <div className="journey-timeline">
            <div className="timeline-line"></div>
            <div className="journey-grid">
              {companyJourney.map((milestone, index) => (
                <div key={index} className="journey-item" style={{transitionDelay: `${index * 0.2}s`}}>
                  <div className="journey-content">
                    <div className="journey-header">
                      <h3 className="journey-title">{milestone.title}</h3>
                    </div>
                    <p className="journey-description">{milestone.description}</p>
                  </div>
                  <div className="journey-connector"></div>
                  <div className="journey-number">
                    {milestone.step}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      {/* <div className="vision-section">
        <div className="section-container">
          <div className="vision-content">
            <div className="vision-text">
              <div className="section-badge vision-badge">
                <Globe className="badge-icon" />
                <span>Our Mission</span>
              </div>
              <h2 className="vision-title">
                Our Vision for the Future
              </h2>
              <p className="vision-description">
                To become India's most trusted source for heavy machinery parts through a commitment to quality, affordability, and customer satisfaction. We envision a future where equipment downtime is minimized and productivity is maximized.
              </p>
            </div>
            <div className="vision-image">
              <img 
                src={vision}
                alt="Heavy Equipment at Construction Site" 
                className="vision-img"
              />
            </div>
          </div>
        </div>
      </div> */}





      {/* Vision Section */}
      <div className="vision-section">
        <div className="section-container">
          <div className="vision-content">
            <div className="vision-text">
              <div className="section-badge vision-badge">
                <Globe className="badge-icon" />
                <span>Our Mission</span>
              </div>
              <h2 className="vision-title">
                Our Vision for the Future
              </h2>
              <p className="vision-description">
                To become India's most trusted source for heavy machinery parts through a commitment to quality, affordability, and customer satisfaction. We envision a future where equipment downtime is minimized and productivity is maximized.
              </p>
            </div>
            <div className="vision-image">
              <img 
                src={vision}
                alt="Heavy Equipment at Construction Site" 
                className="vision-img"
              />
            </div>
          </div>
        </div>
      </div>












      {/* Core Values */}
      <section className="values-section">
        <div className="container">
          <h2>Our Core Values</h2>
          <div className="values-grid">
            <div className="value-item">
              <div className="value-icon">
                <Award size={32} />
              </div>
              <h3 className="value-title">Innovation</h3>
              <p className="value-description">
                Continuously evolving to meet the changing needs of the heavy machinery industry with cutting-edge solutions.
              </p>
            </div>
            <div className="value-item">
              <div className="value-icon">
                <Award size={32} />
              </div>
              <h3 className="value-title">Durability</h3>
              <p className="value-description">
                Providing spare parts that withstand the toughest environments and deliver consistent performance.
              </p>
            </div>
            <div className="value-item">
              <div className="value-icon">
                <Award size={32} />
              </div>
              <h3 className="value-title">Prompt Service</h3>
              <p className="value-description">
                Ensuring quick response times and efficient delivery to minimize equipment downtime.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Directors;